import React from 'react'
import { useLabraChatSelector } from '../../modules/labraChat/context'
import {
  selectIsLabraChatPanelOpen,
  selectLabraChatLayout,
} from '../../modules/labraChat/selectors'
import { LabraAIChatPanel, LabraAIChatPanelProps } from './LabraAIChatPanel'
import { LabraChatDockContext } from './dockContext'
import { useChatDockWidth } from './useChatDockWidth'
import { useStyles } from './LabraChatDock.styles'

export interface LabraChatDockProps extends LabraAIChatPanelProps {
  children: React.ReactNode
}

/**
 * Puts the chat beside the page instead of over it: the page keeps its own
 * column and gives up width as the dock is dragged wider, so the user can carry
 * on working while the agent answers. Wrap a page in it — no host layout needed.
 */
export const LabraChatDock: React.FC<LabraChatDockProps> = ({
  children,
  ...panelProps
}) => {
  const { classes, cx } = useStyles()
  const isPanelOpen = useLabraChatSelector(selectIsLabraChatPanelOpen)
  const layout = useLabraChatSelector(selectLabraChatLayout)
  // Only a sidebar takes a column of its own; the other layouts float above the
  // page. Until the user picks one, the host's own variant decides.
  const isSidebar = layout
    ? layout === 'sidebar'
    : panelProps.variant === 'docked'
  const dock = useChatDockWidth()

  return (
    <LabraChatDockContext.Provider value>
      <div className={classes.split} ref={dock.containerRef}>
        <div className={classes.main}>{children}</div>

        {!isSidebar && <LabraAIChatPanel {...panelProps} />}

        {isPanelOpen && isSidebar && (
          <aside
            className={classes.dock}
            ref={dock.dockRef}
            style={dock.width === undefined ? undefined : { width: dock.width }}
          >
            <div
              className={cx(
                classes.resizeHandle,
                dock.isResizing && classes.resizing
              )}
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize the chat panel"
              aria-valuemin={dock.minWidth}
              aria-valuenow={dock.width}
              tabIndex={0}
              onPointerDown={dock.startResize}
              onKeyDown={dock.nudge}
              onDoubleClick={dock.reset}
            />
            <LabraAIChatPanel {...panelProps} variant="docked" />
          </aside>
        )}
      </div>
    </LabraChatDockContext.Provider>
  )
}
